/**
 * Список фізичних осіб. ч.1059 (рішення власника): картка фізособи —
 * окрема сторінка /individuals/new і /individuals/:id (IndividualCardPage)
 * з чернеткою незбережених правок; тут лише список.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { individuals, type Individual } from '@/api/hr';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

export default function IndividualsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  // 2026-08-23 (Норма #29): серверна пагінація + сортування.
  const refreshRef = useRef<(() => void) | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<Individual>(
    (limit, offset, sort) => individuals.list({
      q: q || undefined, sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [q],
    { attachmentsType: 'ref.individuals' },
  );

  // Старі посилання /individuals?open=<id> (закладки, історія браузера) —
  // на нову адресу картки.
  const [searchParams] = useSearchParams();
  const openId = searchParams.get('open');
  useEffect(() => {
    if (openId) navigate(`/individuals/${openId}`, { replace: true });
  }, [openId, navigate]);

  const cols = useMemo<ColDef<Individual>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: Individual }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    {
      field: 'full_name', headerName: 'ПІБ', flex: 1, minWidth: 280,
      cellRenderer: (p: { data?: Individual }) => !p.data ? '' : (
        <>
          {p.data.full_name}
          {p.data.needs_review && (
            <span
              title="Реквізити з 1С не відповідають стандарту (напр. дубль-картка з одним ІПН або битий РНОКПП). Відкрийте картку → виправте."
              style={{
                background: '#fde68a', color: 'var(--badge-amber-fg)', borderRadius: 4,
                padding: '2px 6px', fontSize: 12, fontWeight: 600,
                marginLeft: 8, whiteSpace: 'nowrap',
              }}
            >
              ⚠ Карантин
            </span>
          )}
        </>
      ),
    },
    { field: 'inn', headerName: 'ІПН', width: 140, valueFormatter: (p) => p.data ? (p.value ?? '—') : '' },
    {
      field: 'birth_date', headerName: 'Дата народження', width: 150,
      valueFormatter: (p) => p.data ? (p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '—') : '',
    },
    { field: 'phone', headerName: 'Телефон', width: 150, sortable: false, valueFormatter: (p) => p.data ? (p.value ?? '—') : '' },
    // Iконки ✎/× прибрано — редагування через double-click по рядку.
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
  ], []);

  return (
    <div className="card">
      <div className="toolbar with-filters" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Фізичні особи<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/individuals/new')}>
          + Нова фізособа
        </button>
      </div>
      <div className="filters">
        <input placeholder="ПІБ або ІПН…" value={q}
               onChange={(e) => setQ(e.target.value)}
               style={{ width: 160, flex: '0 0 auto' }} />
      </div>
      <InfiniteDocGrid<Individual>
        datasource={datasource}
        columnDefs={cols}
        storageKey="individuals-list"
        onRowDoubleClicked={(i) => navigate(`/individuals/${i.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
        refreshRef={refreshRef}
      />
    </div>
  );
}
